/**
 * Apps API client (003-multi-app-platform, T026; extended in 015 US8 T155).
 * GET /api/v1/apps — list of apps the current user may see, each carrying
 * the user's per-app `user_role` (name + permissions). `user_role` carries
 * app-scoped capabilities only (e.g. `trade`) — it is never an admin marker.
 * Platform admin authority is `user.is_admin` alone (spec 020 §10).
 */
import { api } from './api';

export type AppStatus = 'active' | 'coming_soon' | 'disabled';

export interface UserRoleForApp {
  name: string;
  permissions: string[];
}

export interface App {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  route_path: string;
  status: AppStatus;
  theme_id: string | null;
  display_order: number;
  /**
   * Current user's per-app role (e.g. `trader`). Never an admin marker —
   * admin is `user.is_admin`.
   */
  user_role: UserRoleForApp | null;
}

export async function getApps(): Promise<App[]> {
  const list = await api.get<App[]>('/apps');
  if (!Array.isArray(list)) return [];
  // Defensive default — backend should always send `user_role` (null or object),
  // but older deployments may omit it. Normalize to null for consumer ergonomics.
  return list.map((a) => ({
    ...a,
    user_role: a.user_role ?? null,
  }));
}
